import { z } from 'zod';
/**
 * Canonical submission state machine. Spec §4.1, Appendix D.
 *
 * Zip lane:
 *   validating → validation_failed (terminal)
 *              → upload_failed     (admin-requeueable)
 *              → scan_failed       (admin-requeueable)
 *              → pending_review    → approving → pr_opened → approved (terminal)
 *                                  → rejected (terminal)
 *              → approving         (auto-approve lane)
 *
 * PR lane enters at `approved` directly.
 */
export const submissionStatusSchema = z.enum([
    'validating',
    'validation_failed',
    'upload_failed',
    'scan_failed',
    'pending_review',
    'approving',
    'pr_opened',
    'approved',
    'rejected',
]);
export const submissionTypeSchema = z.enum(['zip', 'pr']);
/**
 * Structured validator finding. Mirrors the `ValidationIssue` shape emitted
 * by `@cotality/marketplace-validator`; kept as a local Zod definition to
 * avoid a contracts→validator runtime dependency.
 */
export const validationIssueSchema = z.object({
    severity: z.enum(['error', 'warning']),
    scanner: z.enum(['schema', 'secret', 'audit', 'heuristic']),
    file: z.string().optional(),
    message: z.string(),
    details: z.unknown().optional(),
});
export const validationLogSchema = z.object({
    ok: z.boolean(),
    issues: z.array(validationIssueSchema),
    pluginMeta: z
        .object({
        name: z.string(),
        version: z.string(),
        category: z.string(),
        keywords: z.array(z.string()),
        skills: z.array(z.object({ name: z.string(), description: z.string() })),
    })
        .optional(),
});
export const submissionSchema = z.object({
    id: z.string().uuid(),
    submitterId: z.string().uuid(),
    teamId: z.string().uuid().nullable(),
    /** Null until validator resolves `plugin.json#name`; stays null on first-publish until approved. */
    pluginId: z.string().uuid().nullable(),
    type: submissionTypeSchema,
    status: submissionStatusSchema,
    validationLog: validationLogSchema.nullable(),
    uploadedFiles: z.unknown().nullable(),
    version: z.string().nullable(),
    adoPrUrl: z.string().url().nullable(),
    submittedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable(),
});
/** Admin queue row — adds submitter + team ref. */
export const submissionQueueItemSchema = submissionSchema.extend({
    submitter: z.object({
        id: z.string().uuid(),
        displayName: z.string().nullable(),
        email: z.string().email(),
    }),
    team: z
        .object({ id: z.string().uuid(), name: z.string(), displayName: z.string().nullable() })
        .nullable(),
    pluginName: z.string().nullable(),
});
//# sourceMappingURL=submission.js.map